import { useMemo } from "react";

import type {
  FeatureFlagsAppName as AppName,
  FeatureFlagsDataEnvelope,
} from "../../../models/featureFlags";
import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../services/query";

import { useFeatureFlagsQuery } from "./featureFlags.query";

export * from "./featureFlags.invalidate";
export * from "./featureFlags.static";

export const featureFlagsAccessError = new Error(
  "Attempted to access feature flags data but it is not available",
);

// ###########################
// ## useFeatureFlagsStatus ##
// ###########################

export type ParamsOfUseFeatureFlagsStatus = {
  app: AppName;
};
export type ResultOfUseFeatureFlagsStatus = QueryStatus;
export type UseFeatureFlagsStatus = (
  params: ParamsOfUseFeatureFlagsStatus,
) => ResultOfUseFeatureFlagsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of feature flags data.
 */
export const useFeatureFlagsStatus: UseFeatureFlagsStatus = ({ app }) => {
  return useFeatureFlagsQuery({ app, watch: ["status"] }).status;
};

// ##########################
// ## useFeatureFlagsError ##
// ##########################

export type ParamsOfUseFeatureFlagsError = {
  app: AppName;
};
export type ResultOfUseFeatureFlagsError = Error | null;
export type UseFeatureFlagsError = (
  params: ParamsOfUseFeatureFlagsError,
) => ResultOfUseFeatureFlagsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of feature flags data
 * or null if no error occurred.
 */
export const useFeatureFlagsError: UseFeatureFlagsError = ({ app }) => {
  return useFeatureFlagsQuery({ app, watch: ["error"] }).error;
};

// ##############################
// ## useMaybeFeatureFlagsData ##
// ##############################

export type ParamsOfUseMaybeFeatureFlagsData = {
  app: AppName;
};
export type ResultOfUseMaybeFeatureFlagsData =
  | FeatureFlagsDataEnvelope
  | undefined;
export type UseMaybeFeatureFlagsData = (
  params: ParamsOfUseMaybeFeatureFlagsData,
) => ResultOfUseMaybeFeatureFlagsData;

/**
 * The returned value of this hook will contain
 * feature flags data, which may be undefined.
 */
export const useMaybeFeatureFlagsData: UseMaybeFeatureFlagsData = ({ app }) => {
  return useFeatureFlagsQuery({
    app,
    watch: ["data"],
  }).data;
};

// #########################
// ## useFeatureFlagsData ##
// #########################

export type ParamsOfUseFeatureFlagsData = {
  app: AppName;
};
export type ResultOfUseFeatureFlagsData = FeatureFlagsDataEnvelope;
export type UseFeatureFlagsData = (
  params: ParamsOfUseFeatureFlagsData,
) => ResultOfUseFeatureFlagsData;

/**
 * The returned value of this hook will contain feature flags data.
 *
 * This hook is guaranteed to contain feature flags data or will throw.
 */
export const useFeatureFlagsData: UseFeatureFlagsData = ({ app }) => {
  const data = useMaybeFeatureFlagsData({ app });
  if (data === undefined) {
    throw featureFlagsAccessError;
  }
  return data;
};

// ##########################
// ## useMaybeFeatureFlags ##
// ##########################

export type ParamsOfUseMaybeFeatureFlags = {
  app: AppName;
  forceDisable?: boolean;
};
export type ResultOfUseMaybeFeatureFlags = QueryEnvelope<
  FeatureFlagsDataEnvelope,
  Error
>;
export type UseMaybeFeatureFlags = (
  params: ParamsOfUseMaybeFeatureFlags,
) => ResultOfUseMaybeFeatureFlags;

/**
 * The `data` property returned from this hook will contain
 * feature flags data, which may be undefined.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of feature flags data.
 */
export const useMaybeFeatureFlags: UseMaybeFeatureFlags = ({
  app,
  forceDisable,
}) => {
  const { status, error, data } = useFeatureFlagsQuery({
    app,
    watch: ["status", "error", "data"],
    forceDisable,
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// #####################
// ## useFeatureFlags ##
// #####################

export type ParamsOfUseFeatureFlags = {
  app: AppName;
};
export type ResultOfUseFeatureFlags = EnsuredQueryEnvelope<
  FeatureFlagsDataEnvelope,
  Error
>;
export type UseFeatureFlags = (
  params: ParamsOfUseFeatureFlags,
) => ResultOfUseFeatureFlags;

/**
 * The `data` property returned from this hook will contain
 * feature flags data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of feature flags data.
 *
 * This hook is guaranteed to contain feature flags data or will throw.
 */
export const useFeatureFlags: UseFeatureFlags = ({ app }) => {
  const { status, error, data } = useFeatureFlagsQuery({
    app,
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw featureFlagsAccessError;
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
